/**
 * Customer Preprocessing Manager - Staged Workflow Component
 * 
 * This component orchestrates the complete customer preprocessing workflow in stages:
 * 1. Preprocessing Decision (YES/NO buttons)
 * 2. Position Type Configuration (Enhanced PositionTypeSelector)  
 * 3. Supplier Case Creation (Enhanced QuoteAggregationManager with full configuration)
 * 4. Final Compilation (Enhanced CustomerQuoteCompilationManager)
 * 
 * Created: August 28, 2025
 * Architecture: Staged progression with workflow state integration
 * Enhancement: Uses QuoteAggregationManager for complete supplier case setup
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Paper,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Block as NoPreprocessingIcon,
  Settings as ConfigureIcon,
  Send as GenerateIcon,
  CheckCircle as CompleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { CaseData } from '../../../services/caseManagementService';
import { useNotification } from '../../../contexts/NotificationContext';
import { PositionTypeSelector, PositionTypeSelection } from '../integration/PositionTypeSelector';
import { CustomerQuoteCompilationManager } from './CustomerQuoteCompilationManager';
import { SupplierQuoteProgressTracker } from './SupplierQuoteProgressTracker';
import QuoteAggregationManager from '../integration/QuoteAggregationManager';

interface CustomerPreprocessingManagerProps {
  selectedCase: CaseData;
  currentWorkflowState: string;
  onWorkflowTransition: (fromState: string, toState: string) => Promise<void>;
  onStageComplete?: (stage: number, stageData: any) => void;
}

interface CustomerQuotePosition {
  id: string;
  position_number: number;
  part_name: string;
  part_number: string;
  notes: string;
  quantity: number;
  type_specific_data: any;
}

// Preprocessing stages configuration
const PREPROCESSING_STAGES = [
  {
    id: 'decision',
    label: 'Preprocessing Decision',
    description: 'Decide if supplier quotes are needed for customer positions',
    icon: <InfoIcon />
  },
  {
    id: 'configure',
    label: 'Position Configuration',
    description: 'Configure supplier quote types for each position',
    icon: <ConfigureIcon />
  },
  {
    id: 'generate',
    label: 'Generate Supplier Quotes',
    description: 'Create supplier cases and monitor responses',
    icon: <GenerateIcon />
  },
  {
    id: 'compile',
    label: 'Final Compilation',
    description: 'Compile customer quote with all supplier responses',
    icon: <CompleteIcon />
  }
];

export const CustomerPreprocessingManager: React.FC<CustomerPreprocessingManagerProps> = ({
  selectedCase,
  currentWorkflowState,
  onWorkflowTransition,
  onStageComplete
}) => {
  const { showNotification } = useNotification();
  
  // State management
  const [currentStage, setCurrentStage] = useState(0);
  const [stageData, setStageData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [customerPositions, setCustomerPositions] = useState<CustomerQuotePosition[]>([]);
  const [positionTypeSelections, setPositionTypeSelections] = useState<Map<string, PositionTypeSelection>>(new Map());
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set([0]));
  const [supplierQuotesGenerated, setSupplierQuotesGenerated] = useState<any[]>([]);
  const [supplierCaseStatuses, setSupplierCaseStatuses] = useState<Map<string, string>>(new Map());
  const [showCompilationManager, setShowCompilationManager] = useState(false);
  const [showQuoteAggregationManager, setShowQuoteAggregationManager] = useState(false);
  
  // � PERFORMANCE FIX: Add loading guards to prevent infinite loops
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastLoadedCaseId, setLastLoadedCaseId] = useState<string | null>(null);

  // �🐛 COMPREHENSIVE DEBUG LOGGING (Reduced frequency to prevent spam)
  useEffect(() => {
    console.log('\n🐛 ===== CUSTOMER PREPROCESSING MANAGER DEBUG =====');
    console.log('📊 State Update:', {
      currentStage,
      customerCaseId: selectedCase?.id,
      positionsCount: customerPositions.length,
      selectedTypesCount: positionTypeSelections.size,
      workflowState: currentWorkflowState,
      updatedAt: new Date().toISOString()
    });
    console.log('🎯 Current positions:', customerPositions.map(p => ({
      id: p.id,
      position_number: p.position_number,
      part_name: p.part_name,
      quantity: p.quantity,
      type_specific_data: p.type_specific_data
    })));
    console.log('🎯 Position type selections:', Array.from(positionTypeSelections.entries()));
    console.log('🎯 Stage data:', stageData);
    console.log('🎯 Supplier quotes generated:', supplierQuotesGenerated);
    console.log('====================================================\n');
  }, [currentStage, customerPositions.length, positionTypeSelections.size, currentWorkflowState]); // 🔧 FIXED: More stable dependencies

  // 🔧 OPTIMIZED: Load customer positions only when case changes
  useEffect(() => {
    const caseId = selectedCase?.id;
    if (caseId && caseId !== lastLoadedCaseId) {
      console.log('🔄 [PREPROCESSING-DEBUG] New case detected, loading positions', {
        caseId,
        previousCaseId: lastLoadedCaseId,
        workflowState: currentWorkflowState,
        caseName: selectedCase?.customer_name
      });
      setIsInitializing(true);
      setLastLoadedCaseId(caseId);
      loadCustomerPositions();
    }
  }, [selectedCase?.id]); // 🔧 FIXED: Only depend on case ID change

  // 🔧 OPTIMIZED: Single effect for stage determination with proper guards
  useEffect(() => {
    console.log('🔄 [PREPROCESSING-DEBUG] Checking if ready for stage determination', {
      isInitializing,
      loading,
      hasPositions: customerPositions.length > 0,
      hasPositionSelections: positionTypeSelections.size > 0,
      hasSupplierQuotes: supplierQuotesGenerated.length > 0,
      currentWorkflowState,
      caseId: selectedCase?.id
    });
    
    // 🔧 GUARD: Only determine stage when fully loaded and in appropriate workflow state
    if (!isInitializing && 
        !loading && 
        selectedCase?.id && 
        (currentWorkflowState === 'preprocessing_needed' || currentWorkflowState === 'waiting_for_customer_decision')) {
      console.log('🎯 [PREPROCESSING-DEBUG] Ready to determine stage - all conditions met');
      determineCurrentStage();
    }
  }, [
    isInitializing, 
    loading, 
    customerPositions.length, 
    positionTypeSelections.size, 
    supplierQuotesGenerated.length, 
    currentWorkflowState,
    selectedCase?.id
  ]); // 🔧 FIXED: Stable dependencies using counts instead of arrays

  const loadExistingSupplierQuotes = async () => {
    if (!selectedCase) {
      console.log('🔍 [SUPPLIER-QUOTES-DEBUG] No selected case - skipping supplier quotes load');
      return;
    }
    
    console.log('🔍 [SUPPLIER-QUOTES-DEBUG] Loading existing supplier quotes for case:', selectedCase.id);
    console.log('🔍 [SUPPLIER-QUOTES-DEBUG] API URL:', `/api/v3/cases/${selectedCase.id}/supplier-quote-mappings`);
    
    try {
      const response = await fetch(`/api/v3/cases/${selectedCase.id}/supplier-quote-mappings`);
      console.log('📡 [SUPPLIER-QUOTES-DEBUG] Response status:', response.status, response.statusText);
      
      const data = await response.json();
      
      console.log('📥 [SUPPLIER-QUOTES-DEBUG] Supplier quotes API response:', {
        success: data.success,
        message: data.message,
        mappingsLength: data.mappings?.length || 0,
        dataLength: data.data?.length || 0,
        fullResponse: data
      });
      
      // API returns data in 'mappings' property, not 'data'
      const supplierQuotes = data.mappings || data.data || [];
      
      if (data.success && supplierQuotes.length > 0) {
        setSupplierQuotesGenerated(supplierQuotes);
        console.log('✅ [SUPPLIER-QUOTES-DEBUG] Loaded existing supplier quotes:', {
          count: supplierQuotes.length,
          quotes: supplierQuotes.map((q: any) => ({
            id: q.id,
            supplier_case_id: q.supplier_case_id,
            customer_quote_position_id: q.customer_quote_position_id,
            quoted_price: q.quoted_price,
            supplier_case_number: q.supplier_case_number
          }))
        });
        console.log('🎯 [SUPPLIER-QUOTES-DEBUG] Supplier quotes state updated, triggering stage re-evaluation');
        
        // Load supplier case workflow states for Final Compilation stage logic
        await loadSupplierCaseStates();
      } else {
        console.log('📭 [SUPPLIER-QUOTES-DEBUG] No existing supplier quotes found');
        console.log('🔍 [SUPPLIER-QUOTES-DEBUG] Reason:', {
          success: data.success,
          hasMappings: !!data.mappings,
          mappingsLength: data.mappings?.length,
          hasData: !!data.data,
          dataLength: data.data?.length,
          message: data.message
        });
        setSupplierQuotesGenerated([]);
      }
    } catch (error) {
      console.error('❌ [SUPPLIER-QUOTES-DEBUG] Error loading existing supplier quotes:', error);
      setSupplierQuotesGenerated([]);
    }
  };

  const loadSupplierCaseStates = async () => {
    if (!selectedCase) return;
    
    try {
      console.log('🔍 [SUPPLIER-STATES-DEBUG] Loading supplier case states for customer case:', selectedCase.id);
      
      const response = await fetch(`/api/v3/cases/${selectedCase.id}/supplier-cases`);
      const data = await response.json();
      
      console.log('📡 [SUPPLIER-STATES-DEBUG] Supplier cases API response:', {
        success: data.success,
        supplierCasesCount: data.supplier_cases?.length || 0,
        fullResponse: data
      });
      
      if (data.success && data.supplier_cases) {
        const statusMap = new Map<string, string>();
        
        console.log('🔍 [SUPPLIER-STATES-DEBUG] Processing supplier cases:');
        data.supplier_cases.forEach((supplierCase: any, index: number) => {
          statusMap.set(supplierCase.id, supplierCase.workflow_state);
          
          // EXTENSIVE METADATA DEBUG
          const selectedQuote = supplierCase.metadata?.selected_quote;
          const quoteResponses = supplierCase.metadata?.quote_responses || [];
          const selectedQuoteFromResponses = quoteResponses.find((q: any) => q.selected || q.is_selected);
          
          console.log(`� [SUPPLIER-STATES-DEBUG] Supplier Case ${index + 1}:`, {
            case_number: supplierCase.case_number,
            workflow_state: supplierCase.workflow_state,
            has_selected_quote_object: !!selectedQuote,
            selected_quote_id: selectedQuote?.quote_id,
            quote_responses_count: quoteResponses.length,
            selected_quote_from_responses: selectedQuoteFromResponses ? {
              quote_id: selectedQuoteFromResponses.quote_id,
              selected: selectedQuoteFromResponses.selected,
              is_selected: selectedQuoteFromResponses.is_selected,
              supplier_name: selectedQuoteFromResponses.supplier_name,
              total_value: selectedQuoteFromResponses.total_quote_value
            } : null,
            metadata_keys: Object.keys(supplierCase.metadata || {})
          });
        });
        
        setSupplierCaseStatuses(statusMap);
        
        const casesWithQuoteSelected = data.supplier_cases.filter((c: any) => c.workflow_state === 'quote_selected').length;
        const casesWithSelectedQuoteMetadata = data.supplier_cases.filter((c: any) => {
          return c.metadata?.selected_quote || (c.metadata?.quote_responses || []).some((q: any) => q.selected || q.is_selected);
        }).length;
        
        console.log('🎯 [SUPPLIER-STATES-DEBUG] Summary:', {
          totalCases: data.supplier_cases.length,
          casesWithQuoteSelected,
          casesWithSelectedQuoteMetadata,
          workflowStates: Object.fromEntries(statusMap),
          should_activate_final_compilation: casesWithQuoteSelected > 0 && casesWithSelectedQuoteMetadata > 0
        });
      } else {
        console.log('❌ [SUPPLIER-STATES-DEBUG] Failed to load supplier cases or no cases found');
      }
    } catch (error) {
      console.error('❌ [SUPPLIER-STATES-DEBUG] Error loading supplier case states:', error);
    }
  };

  const loadCustomerPositions = async () => {
    if (!selectedCase) {
      console.log('🚫 [PREPROCESSING-DEBUG] No case selected, skipping position loading');
      setIsInitializing(false);
      return;
    }
    
    console.log('📋 [PREPROCESSING-DEBUG] Loading customer positions for case:', selectedCase.id);
    setLoading(true);
    try {
      const response = await fetch(`/api/v3/cases/${selectedCase.id}/positions`);
      const data = await response.json();
      
      console.log('📥 [PREPROCESSING-DEBUG] Positions API response:', data);
      
      if (data.success) {
        setCustomerPositions(data.data || []);
        console.log('✅ [PREPROCESSING-DEBUG] Loaded customer positions:', {
          count: data.data?.length || 0,
          positions: data.data?.map((p: any) => ({
            id: p.id,
            position_number: p.position_number,
            part_name: p.part_name,
            type_specific_data: p.type_specific_data
          }))
        });
        
        // Load existing configurations from positions
        if (data.data?.length > 0) {
          const existingSelections = new Map();
          data.data.forEach((position: any) => {
            console.log('🔍 [POSITION-CONFIG-DEBUG] Checking position for configurations:', {
              positionId: position.id,
              positionNumber: position.position_number,
              hasTypeSpecificData: !!position.type_specific_data,
              selectedTypes: position.type_specific_data?.selected_types,
              typeConfigurations: position.type_specific_data?.type_configurations
            });
            
            if (position.type_specific_data?.selected_types) {
              existingSelections.set(position.id, {
                selectedTypes: position.type_specific_data.selected_types,
                typeConfigurations: position.type_specific_data.type_configurations || {},
                quantity: position.quantity,
                positionNumber: position.position_number,
                customerQuantity: position.quantity
              });
              console.log('✅ [POSITION-CONFIG-DEBUG] Found existing configuration for position:', position.id);
            } else {
              console.log('📭 [POSITION-CONFIG-DEBUG] No configuration found for position:', position.id);
            }
          });
          
          console.log('🔧 [PREPROCESSING-DEBUG] Restored position type selections:', {
            count: existingSelections.size,
            selections: Object.fromEntries(existingSelections)
          });
          setPositionTypeSelections(existingSelections);
        }

        // Load existing supplier quotes
        console.log('🚀 [PREPROCESSING-DEBUG] About to call loadExistingSupplierQuotes...');
        await loadExistingSupplierQuotes();
        console.log('✅ [PREPROCESSING-DEBUG] loadExistingSupplierQuotes call completed');
        
        // Stage determination will be handled by useEffect when loading completes
        console.log('🎯 [PREPROCESSING-DEBUG] All data loaded, useEffect will handle stage determination');
      } else {
        console.error('❌ [PREPROCESSING-DEBUG] Failed to load customer positions:', data.message);
        setCustomerPositions([]);
      }
    } catch (error) {
      console.error('❌ [PREPROCESSING-DEBUG] Error loading customer positions:', error);
      setCustomerPositions([]);
    } finally {
      setLoading(false);
      setIsInitializing(false); // 🔧 CRITICAL: Mark initialization complete
    }
  };

  const determineCurrentStage = async () => {
    console.log('🎯 [STAGE-DEBUG] determineCurrentStage called with:', {
      currentWorkflowState,
      supplierQuotesCount: supplierQuotesGenerated.length,
      positionConfigurationsCount: positionTypeSelections.size,
      preprocessingDecision: stageData.preprocessingDecision
    });

    // For preprocessing_needed, get actual progress from API
    if (currentWorkflowState === 'preprocessing_needed') {
      try {
        console.log('🔄 [STAGE-DEBUG] Fetching preprocessing progress from API...');
        const response = await fetch(`/api/v3/customer-preprocessing/${selectedCase.id}/progress`);
        const data = await response.json();
        
        if (data.success) {
          const progress = data.data.preprocessing_progress;
          console.log('📊 [STAGE-DEBUG] API Progress data:', progress);
          
          const hasPositionConfigurations = progress.positions_configured > 0;
          const hasSupplierCases = progress.supplier_cases_created > 0;
          
          // Calculate expected supplier cases based on position configurations
          const expectedSupplierCases = customerPositions.reduce((total, position) => {
            const selectedTypes = position.type_specific_data?.selected_types || [];
            return total + selectedTypes.filter((type: string) => type !== 'no_supplier_quotes').length;
          }, 0);
          
          console.log('🎯 [STAGE-DEBUG] Calculated completion status:', {
            hasPositionConfigurations,
            hasSupplierCases,
            expectedSupplierCases,
            actualSupplierCases: progress.supplier_cases_created,
            configurationPercentage: progress.configuration_percentage
          });
          
          let activeStage = 1; // Default to Position Configuration
          
          // Check supplier case workflow states for Final Compilation logic
          const supplierCasesWithQuotesSelected = Array.from(supplierCaseStatuses.values()).filter(state => state === 'quote_selected').length;
          
          if (hasPositionConfigurations && hasSupplierCases && supplierCasesWithQuotesSelected > 0) {
            // Configurations, supplier cases exist, AND quotes are selected → Final Compilation
            activeStage = 3;
            console.log('🎯 [STAGE-DEBUG] Setting stage 3 (Final Compilation) - configs, supplier cases, and quotes selected', {
              supplierCasesWithQuotesSelected,
              totalSupplierCases: supplierCaseStatuses.size,
              supplierStates: Object.fromEntries(supplierCaseStatuses)
            });
          } else if (hasPositionConfigurations && hasSupplierCases) {
            // Both configurations and supplier cases exist but no quotes selected → Generate Quotes (completed), wait for quotes
            activeStage = 3; // Still go to Final Compilation to monitor quote selection progress
            console.log('🎯 [STAGE-DEBUG] Setting stage 3 (Final Compilation) - waiting for quote selection', {
              supplierCasesWithQuotesSelected,
              totalSupplierCases: supplierCaseStatuses.size
            });
          } else if (hasPositionConfigurations) {
            // Configurations exist but no supplier cases → Generate Quotes
            activeStage = 2;
            console.log('🎯 [STAGE-DEBUG] Setting stage 2 (Generate Quotes) - configs exist, need supplier cases');
          } else {
            // No configurations → Position Configuration
            activeStage = 1;
            console.log('🎯 [STAGE-DEBUG] Setting stage 1 (Position Config) - no configurations found');
          }
          
          setCurrentStage(activeStage);
          setExpandedStages(new Set([activeStage]));
          return;
        }
      } catch (error) {
        console.error('❌ [STAGE-DEBUG] Error fetching progress:', error);
        // Fall back to local state logic
      }
    }

    // Handle other workflow states
    switch (currentWorkflowState) {
      case 'case_created':
      case 'preprocessing_decision':
        console.log('🎯 [STAGE-DEBUG] Setting stage 0 (Decision) - workflow state:', currentWorkflowState);
        setCurrentStage(0);
        setExpandedStages(new Set([0]));
        break;
      case 'preprocessing_needed':
        // Fallback logic if API call failed
        const hasPositionConfigurations = positionTypeSelections.size > 0;
        const hasSupplierCases = supplierQuotesGenerated.length > 0;
        
        let activeStage = 1;
        if (hasPositionConfigurations && hasSupplierCases) {
          activeStage = 3;
        } else if (hasPositionConfigurations) {
          activeStage = 2;
        }
        
        setCurrentStage(activeStage);
        setExpandedStages(new Set([activeStage]));
        break;
      case 'waiting_for_customer_decision':
        if (stageData.preprocessingDecision === 'no') {
          console.log('🎯 [STAGE-DEBUG] Setting stage 0 (Decision completed) - preprocessing skipped');
          setCurrentStage(0);
        } else {
          console.log('🎯 [STAGE-DEBUG] Setting stage 3 (Compilation) - waiting for customer decision');
          setCurrentStage(3);
        }
        break;
      case 'case_won':
      case 'waiting_for_file_upload':
      case 'quote_selected':
      case 'converted_to_order':
      case 'completed':
      case 'case_closed':
        // Preprocessing is complete for these advanced workflow states
        console.log(`🎯 [STAGE-DEBUG] Setting stage 3 (All Stages Completed) - advanced workflow state: ${currentWorkflowState}`);
        setCurrentStage(3);
        setExpandedStages(new Set([3]));
        break;
      default:
        console.log(`🎯 [STAGE-DEBUG] Unknown workflow state: ${currentWorkflowState} - defaulting to stage 0`);
        setCurrentStage(0);
    }
  };

  // Stage 1: Preprocessing Decision Handler
  const handlePreprocessingDecision = async (decision: 'yes' | 'no') => {
    setLoading(true);
    try {
      console.log(`🎯 [PREPROCESSING] User decision: ${decision}`);
      
      const newStageData = {
        ...stageData,
        preprocessingDecision: decision,
        decisionTimestamp: new Date().toISOString()
      };
      setStageData(newStageData);

      if (decision === 'yes') {
        // Transition to preprocessing_needed state
        await onWorkflowTransition(currentWorkflowState, 'preprocessing_needed');
        setCurrentStage(1);
        setExpandedStages(new Set([1]));
        showNotification('Preprocessing workflow started', 'success');
      } else {
        // Skip preprocessing, go directly to customer decision
        await onWorkflowTransition(currentWorkflowState, 'waiting_for_customer_decision');
        showNotification('Preprocessing skipped - ready for customer decision', 'info');
      }

      onStageComplete?.(0, newStageData);
    } catch (error) {
      console.error('Error handling preprocessing decision:', error);
      showNotification('Error updating workflow state', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Stage 2: Position Type Configuration Handlers
  const handlePositionTypeSelectionChange = (positionId: string, selection: PositionTypeSelection) => {
    const newSelections = new Map(positionTypeSelections);
    newSelections.set(positionId, selection);
    setPositionTypeSelections(newSelections);
    
    console.log('🔧 [PREPROCESSING] Position type selection updated:', {
      positionId,
      selectedTypes: selection.selectedTypes,
      configurationsCount: Object.keys(selection.typeConfigurations || {}).length
    });
  };

  const handleConfigurationComplete = async () => {
    const configuredPositions = Array.from(positionTypeSelections.entries()).filter(([_, selection]) => 
      selection.selectedTypes && selection.selectedTypes.length > 0
    );

    if (configuredPositions.length === 0) {
      showNotification('Please configure at least one position type', 'warning');
      return;
    }

    console.log(`✅ [PREPROCESSING] Configuration complete for ${configuredPositions.length} positions`);
    
    // Save configuration to backend via customer-preprocessing API
    try {
      const positionsToSave = customerPositions.map((position: CustomerQuotePosition) => {
        const selection = positionTypeSelections.get(position.id);
        const typeConfigurations: Record<string, any> = {};
        
        selection?.selectedTypes?.forEach(type => {
          const configs = selection.typeConfigurations || {};
          typeConfigurations[type] = configs[type] || {};
        });
        
        return {
          id: position.id,
          type_specific_data: {
            ...position.type_specific_data,
            selected_types: selection?.selectedTypes || [],
            type_configurations: typeConfigurations
          }
        };
      });

      console.log('💾 [PREPROCESSING] Saving position configurations to backend:', positionsToSave);
      console.log('🎯 [PREPROCESSING] API URL:', `/api/v3/customer-preprocessing/${selectedCase?.id}/save-progress`);
      console.log('📊 [PREPROCESSING] Request payload:', {
        positions: positionsToSave,
        preprocessing_stage: 'position_configuration',
        metadata: {
          positionConfigurations: Object.fromEntries(positionTypeSelections),
          configurationTimestamp: new Date().toISOString()
        }
      });

      console.log('🔄 [PREPROCESSING] Making fetch request...');
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const saveResponse = await fetch(`/api/v3/customer-preprocessing/${selectedCase?.id}/save-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          positions: positionsToSave,
          preprocessing_stage: 'position_configuration',
          metadata: {
            positionConfigurations: Object.fromEntries(positionTypeSelections),
            configurationTimestamp: new Date().toISOString()
          }
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('📡 [PREPROCESSING] Fetch completed, response status:', saveResponse.status);

      if (!saveResponse.ok) {
        const errorText = await saveResponse.text();
        console.error('❌ [PREPROCESSING] Save response error:', {
          status: saveResponse.status,
          statusText: saveResponse.statusText,
          errorText: errorText
        });
        throw new Error(`Failed to save configurations: ${saveResponse.status} ${saveResponse.statusText} - ${errorText}`);
      }

      const saveResult = await saveResponse.json();
      console.log('✅ [PREPROCESSING] Configurations saved successfully:', saveResult);

    } catch (error) {
      console.error('❌ [PREPROCESSING] Error saving configurations:', error);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.error('⏰ [PREPROCESSING] Request timed out after 10 seconds');
          showNotification('Request timed out - Failed to save position configurations', 'error');
        } else {
          console.error('🔍 [PREPROCESSING] Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
          });
          showNotification(`Failed to save position configurations: ${error.message}`, 'error');
        }
      } else {
        showNotification('Failed to save position configurations', 'error');
      }
      return;
    }
    
    setCurrentStage(2);
    setExpandedStages(new Set([2]));
    
    const newStageData = {
      ...stageData,
      positionConfigurations: Object.fromEntries(positionTypeSelections),
      configurationTimestamp: new Date().toISOString()
    };
    setStageData(newStageData);
    onStageComplete?.(1, newStageData);
  };

  // Stage 3: Supplier Quote Generation Handlers
  const handleSupplierQuotesGenerated = (generatedQuotes: any[]) => {
    setSupplierQuotesGenerated(generatedQuotes);
    console.log(`🚀 [PREPROCESSING] Generated ${generatedQuotes.length} supplier quote cases`);
    
    // Start monitoring supplier case statuses
    monitorSupplierCaseStatuses(generatedQuotes);
    
    // Stage progression will be handled automatically by determineCurrentStage()
    console.log('🎯 [PREPROCESSING] Stage 2 complete - supplier quotes created, determineCurrentStage will handle progression');
    
    const newStageData = {
      ...stageData,
      generatedSupplierQuotes: generatedQuotes,
      generationTimestamp: new Date().toISOString()
    };
    setStageData(newStageData);
    onStageComplete?.(2, newStageData);
  };

  const monitorSupplierCaseStatuses = async (supplierQuotes: any[]) => {
    const caseIds = supplierQuotes.map(q => q.supplier_case_id).filter(Boolean);
    
    try {
      const statusChecks = await Promise.all(
        caseIds.map(async (caseId) => {
          try {
            const response = await fetch(`/api/v3/workflow/cases/${caseId}/state`);
            const data = await response.json();
            return {
              caseId,
              status: data.success ? data.data.workflow_state : 'unknown'
            };
          } catch {
            return { caseId, status: 'error' };
          }
        })
      );

      const statusMap = new Map();
      statusChecks.forEach(({ caseId, status }) => {
        statusMap.set(caseId, status);
      });
      
      setSupplierCaseStatuses(statusMap);
      
      // Check if all supplier cases have received responses
      const allResponsesReceived = statusChecks.every(({ status }) => 
        status === 'supplier_response_received' || status === 'quote_selected' || status === 'converted_to_order'
      );
      
      if (allResponsesReceived) {
        console.log('✅ [PREPROCESSING] All supplier responses received - ready for compilation');
        setCurrentStage(3);
        setExpandedStages(new Set([3]));
      }
    } catch (error) {
      console.error('Error monitoring supplier case statuses:', error);
    }
  };

  // Stage 4: Final Compilation Handler
  const handleFinalCompilation = async (compilationData: any) => {
    try {
      console.log('🎯 [PREPROCESSING] Final compilation completed');
      
      // Transition to waiting_for_customer_decision
      await onWorkflowTransition(currentWorkflowState, 'waiting_for_customer_decision');
      
      const newStageData = {
        ...stageData,
        finalCompilation: compilationData,
        compilationTimestamp: new Date().toISOString()
      };
      setStageData(newStageData);
      onStageComplete?.(3, newStageData);
      
      showNotification('Preprocessing completed - ready for customer decision', 'success');
    } catch (error) {
      console.error('Error completing final compilation:', error);
      showNotification('Error completing preprocessing', 'error');
    }
  };

  const toggleStageExpansion = (stageIndex: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageIndex)) {
      newExpanded.delete(stageIndex);
    } else {
      newExpanded.add(stageIndex);
    }
    setExpandedStages(newExpanded);
  };

  const getStageStatus = (stageIndex: number) => {
    console.log(`🎯 [STAGE-STATUS-DEBUG] getStageStatus called for stage ${stageIndex}:`, {
      stageIndex,
      currentStage,
      supplierQuotesCount: supplierQuotesGenerated.length,
      positionConfigurationsCount: positionTypeSelections.size,
      currentWorkflowState
    });

    // For advanced workflow states, all preprocessing stages are completed
    const advancedWorkflowStates = ['case_won', 'waiting_for_file_upload', 'quote_selected', 'converted_to_order', 'completed', 'case_closed'];
    if (advancedWorkflowStates.includes(currentWorkflowState)) {
      console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} completed - advanced workflow state: ${currentWorkflowState}`);
      return 'completed';
    }

    // Simple completion logic based on saved data and workflow state
    const hasPositionConfigurations = positionTypeSelections.size > 0;
    const hasSupplierCases = supplierQuotesGenerated.length > 0;

    // Stage-specific completion logic
    switch (stageIndex) {
      case 0: // Preprocessing Decision
        // Complete if we're in preprocessing_needed or later
        if (currentWorkflowState === 'preprocessing_needed' || currentWorkflowState === 'waiting_for_customer_decision') {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} completed - decision made`);
          return 'completed';
        }
        // Active if we're in decision phase
        if (currentWorkflowState === 'preprocessing_decision' || currentWorkflowState === 'case_created') {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} active - in decision phase`);
          return 'active';
        }
        break;
        
      case 1: // Position Configuration
        // Complete when position configurations exist
        if (hasPositionConfigurations) {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} completed - ${positionTypeSelections.size} positions configured`);
          return 'completed';
        }
        // Active if in preprocessing_needed and current stage is 1
        if (currentWorkflowState === 'preprocessing_needed' && currentStage === 1) {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} active - need position configurations`);
          return 'active';
        }
        break;
        
      case 2: // Generate Supplier Quotes
        // Complete when supplier cases exist
        if (hasSupplierCases) {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} completed - ${supplierQuotesGenerated.length} supplier cases created`);
          return 'completed';
        }
        // Active if position configurations exist and current stage is 2
        if (hasPositionConfigurations && currentStage === 2 && currentWorkflowState === 'preprocessing_needed') {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} active - ready to generate quotes`);
          return 'active';
        }
        break;
        
      case 3: // Final Compilation
        // Complete when customer case transitions to waiting_for_customer_decision or beyond
        if (currentWorkflowState === 'waiting_for_customer_decision' || advancedWorkflowStates.includes(currentWorkflowState)) {
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} completed - customer decision state or beyond reached`);
          return 'completed';
        }
        
        // Active when supplier cases exist AND have quotes selected
        if (hasSupplierCases && currentStage === 3 && currentWorkflowState === 'preprocessing_needed') {
          // Check if all supplier cases have quotes selected
          const supplierCasesWithQuotesSelected = Array.from(supplierCaseStatuses.values()).filter(state => state === 'quote_selected').length;
          const totalSupplierCases = supplierCaseStatuses.size;
          
          console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} Final Compilation check:`, {
            hasSupplierCases,
            currentStage,
            currentWorkflowState,
            supplierCasesWithQuotesSelected,
            totalSupplierCases,
            supplierCaseStates: Object.fromEntries(supplierCaseStatuses)
          });
          
          if (supplierCasesWithQuotesSelected > 0) {
            console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} active - ${supplierCasesWithQuotesSelected}/${totalSupplierCases} supplier cases have quotes selected`);
            return 'active';
          }
        }
        break;
    }
    
    // Default to pending
    console.log(`🎯 [STAGE-STATUS-DEBUG] Stage ${stageIndex} pending - default`);
    return 'pending';
  };

  const canAccessStage = (stageIndex: number) => {
    return stageIndex <= currentStage || getStageStatus(stageIndex) === 'completed';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Preprocessing Overview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Preprocessing Workflow
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Stage-based preprocessing for customer quote positions with supplier quote integration.
              </Typography>
              
              {loading && <LinearProgress sx={{ mb: 2 }} />}
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Current State:</strong> {currentWorkflowState}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Current Stage:</strong> {PREPROCESSING_STAGES[currentStage]?.label}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Customer Positions:</strong> {customerPositions.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Generated Supplier Quotes:</strong> {supplierQuotesGenerated.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Position Configurations:</strong> {positionTypeSelections.size}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Staged Workflow */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preprocessing Stages
              </Typography>
              
              <Stepper orientation="vertical">
                {PREPROCESSING_STAGES.map((stage, index) => (
                  <Step key={stage.id} active={index === currentStage} completed={index < currentStage}>
                    <StepLabel
                      icon={stage.icon}
                      sx={{
                        cursor: canAccessStage(index) ? 'pointer' : 'default',
                        opacity: canAccessStage(index) ? 1 : 0.5
                      }}
                      onClick={() => canAccessStage(index) && toggleStageExpansion(index)}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">
                          {stage.label}
                        </Typography>
                        <Chip 
                          label={getStageStatus(index)} 
                          size="small"
                          color={
                            getStageStatus(index) === 'completed' ? 'success' :
                            getStageStatus(index) === 'active' ? 'primary' : 'default'
                          }
                        />
                        {canAccessStage(index) && (
                          <IconButton size="small">
                            {expandedStages.has(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {stage.description}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Collapse in={expandedStages.has(index)}>
                        <Box sx={{ mt: 2, ml: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                          {/* Stage Content */}
                          {index === 0 && <PreprocessingDecisionStage 
                            onDecision={handlePreprocessingDecision}
                            loading={loading}
                            currentDecision={stageData.preprocessingDecision}
                          />}
                          
                          {index === 1 && <PositionConfigurationStage
                            customerPositions={customerPositions}
                            positionTypeSelections={positionTypeSelections}
                            onSelectionChange={handlePositionTypeSelectionChange}
                            onComplete={handleConfigurationComplete}
                          />}
                          
                          {index === 2 && <Box>
                            <Typography variant="h6" gutterBottom>
                              Supplier Quote Generation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Create supplier cases with proper headers, supplier selection, and file uploads
                            </Typography>
                            
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Supplier Quote Creation</Typography>
                              <Typography variant="body2">
                                Use the Quote Aggregation Manager for comprehensive supplier case creation with supplier selection, headers, and file uploads.
                              </Typography>
                            </Alert>
                            
                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={() => setShowQuoteAggregationManager(true)}
                                disabled={loading}
                                startIcon={<GenerateIcon />}
                              >
                                Open Quote Aggregation Manager
                              </Button>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Full configuration with supplier selection
                              </Typography>
                            </Box>
                            
                            {supplierQuotesGenerated.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Generated Supplier Cases:
                                </Typography>
                                <Grid container spacing={1}>
                                  {supplierQuotesGenerated.map((quote, index) => (
                                    <Grid item key={index}>
                                      <Chip
                                        label={`${quote.supplier_case_number || quote.caseNumber} (${quote.quote_type || quote.type})`}
                                        color="success"
                                        variant="outlined"
                                        size="small"
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            )}
                          </Box>}
                          
                          {index === 3 && (
                            (['case_won', 'waiting_for_file_upload', 'quote_selected', 'converted_to_order', 'completed', 'case_closed'].includes(currentWorkflowState)) ? (
                              <Box>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                  <Typography variant="h6" gutterBottom>
                                    ✅ Preprocessing Completed Successfully
                                  </Typography>
                                  <Typography variant="body2">
                                    All preprocessing stages have been completed. The case has progressed to: <strong>{currentWorkflowState.replace(/_/g, ' ').toUpperCase()}</strong>
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    The customer quote has been compiled and the workflow has advanced beyond the preprocessing phase.
                                  </Typography>
                                </Alert>
                                <SupplierQuoteProgressTracker
                                  customerCaseId={selectedCase.id}
                                  showActions={false} // Read-only mode for completed preprocessing
                                />
                              </Box>
                            ) : (
                              <FinalCompilationStage
                                customerCaseId={selectedCase.id}
                                onCompilationComplete={handleFinalCompilation}
                                onWorkflowTransition={onWorkflowTransition}
                                currentWorkflowState={currentWorkflowState}
                              />
                            )
                          )}
                        </Box>
                      </Collapse>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Quote Aggregation Manager Dialog */}
      <QuoteAggregationManager
        open={showQuoteAggregationManager}
        onClose={() => setShowQuoteAggregationManager(false)}
        customerCaseId={selectedCase.id}
        positions={customerPositions}
        positionTypeSelections={positionTypeSelections}
        onQuotesCreated={handleSupplierQuotesGenerated}
      />
    </Box>
  );
};

// Stage 1: Preprocessing Decision Component
const PreprocessingDecisionStage: React.FC<{
  onDecision: (decision: 'yes' | 'no') => void;
  loading: boolean;
  currentDecision?: string;
}> = ({ onDecision, loading, currentDecision }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Do you need supplier quotes for this customer request?
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      Choose whether to create supplier quote requests for customer positions or proceed directly to customer decision.
    </Typography>
    
    {currentDecision ? (
      <Alert severity={currentDecision === 'yes' ? 'info' : 'success'} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Decision made:</strong> {currentDecision === 'yes' ? 'Start Preprocessing' : 'No Preprocessing Needed'}
        </Typography>
        <Typography variant="body2">
          {currentDecision === 'yes' 
            ? 'Proceeding with position type configuration and supplier quote generation.'
            : 'Skipped preprocessing - customer case ready for decision.'}
        </Typography>
      </Alert>
    ) : (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            startIcon={<StartIcon />}
            onClick={() => onDecision('yes')}
            disabled={loading}
            sx={{ py: 2 }}
          >
            Start Preprocessing
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Configure positions and generate supplier quotes
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            size="large"
            startIcon={<NoPreprocessingIcon />}
            onClick={() => onDecision('no')}
            disabled={loading}
            sx={{ py: 2 }}
          >
            No Preprocessing Needed
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Proceed directly to customer decision
          </Typography>
        </Grid>
      </Grid>
    )}
  </Box>
);

// Stage 1: Position Configuration Component
const PositionConfigurationStage: React.FC<{
  customerPositions: CustomerQuotePosition[];
  positionTypeSelections: Map<string, PositionTypeSelection>;
  onSelectionChange: (positionId: string, selection: PositionTypeSelection) => void;
  onComplete: () => void;
}> = ({ customerPositions, positionTypeSelections, onSelectionChange, onComplete }) => {
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());

  const togglePositionExpansion = (positionId: string) => {
    const newExpanded = new Set(expandedPositions);
    if (newExpanded.has(positionId)) {
      newExpanded.delete(positionId);
    } else {
      newExpanded.add(positionId);
    }
    setExpandedPositions(newExpanded);
  };

  const getConfigurationProgress = () => {
    const configuredCount = Array.from(positionTypeSelections.values()).filter(
      selection => selection.selectedTypes && selection.selectedTypes.length > 0
    ).length;
    return {
      configured: configuredCount,
      total: customerPositions.length,
      percentage: customerPositions.length > 0 ? (configuredCount / customerPositions.length) * 100 : 0
    };
  };

  const progress = getConfigurationProgress();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure Position Types
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Select supplier quote types for each customer position. This determines which supplier cases will be created.
      </Typography>
      
      {/* Progress indicator */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Configuration Progress: {progress.configured}/{progress.total} positions configured
        </Typography>
        <LinearProgress variant="determinate" value={progress.percentage} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {progress.percentage.toFixed(0)}% complete
        </Typography>
      </Box>

      {/* Position configuration list */}
      {customerPositions.map((position) => {
        const selection = positionTypeSelections.get(position.id);
        const isConfigured = selection?.selectedTypes && selection.selectedTypes.length > 0;
        const isExpanded = expandedPositions.has(position.id);

        return (
          <Card key={position.id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1">
                    Position {position.position_number}: {position.part_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {position.part_number} | Quantity: {position.quantity}
                  </Typography>
                  {isConfigured && (
                    <Box sx={{ mt: 1 }}>
                      {selection.selectedTypes.map((type) => (
                        <Chip 
                          key={type} 
                          label={type} 
                          size="small" 
                          color="primary" 
                          sx={{ mr: 1, mb: 1 }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={isConfigured ? 'Configured' : 'Pending'} 
                    color={isConfigured ? 'success' : 'warning'}
                    size="small"
                  />
                  <IconButton 
                    onClick={() => togglePositionExpansion(position.id)}
                    size="small"
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </Box>
              
              <Collapse in={isExpanded}>
                <Divider sx={{ my: 2 }} />
                <PositionTypeSelector
                  position={position}
                  selectedTypes={selection?.selectedTypes || []}
                  onTypeSelectionChange={(types) => {
                    const newSelection: PositionTypeSelection = {
                      ...selection,
                      quantity: position.quantity,
                      positionNumber: position.position_number,
                      customerQuantity: position.quantity,
                      selectedTypes: types,
                      typeConfigurations: selection?.typeConfigurations || {}
                    };
                    onSelectionChange(position.id, newSelection);
                  }}
                  onConfigurationChange={(type, config) => {
                    const newSelection: PositionTypeSelection = {
                      ...selection,
                      quantity: position.quantity,
                      positionNumber: position.position_number,
                      customerQuantity: position.quantity,
                      selectedTypes: selection?.selectedTypes || [],
                      typeConfigurations: {
                        ...selection?.typeConfigurations,
                        [type]: config
                      }
                    };
                    onSelectionChange(position.id, newSelection);
                  }}
                  existingTypeSelections={selection}
                />
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      {/* Complete configuration button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onComplete}
          disabled={progress.configured === 0}
          startIcon={<ConfigureIcon />}
        >
          Complete Configuration ({progress.configured} positions)
        </Button>
      </Box>
    </Box>
  );
};

// Stage 4: Final Compilation Component
const FinalCompilationStage: React.FC<{
  customerCaseId: string;
  onCompilationComplete: (data: any) => void;
  onWorkflowTransition: (fromState: string, toState: string) => Promise<void>;
  currentWorkflowState: string;
}> = ({ customerCaseId, onCompilationComplete, onWorkflowTransition, currentWorkflowState }) => {
  const [showManager, setShowManager] = useState(false);
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Final Customer Quote Compilation
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review and compile supplier quote responses into final customer quote.
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <Button 
          variant="contained" 
          onClick={() => setShowManager(true)}
          sx={{ mr: 2 }}
        >
          Open Quote Compilation Manager
        </Button>
        
        {/* Use SupplierQuoteProgressTracker for progress monitoring */}
        <SupplierQuoteProgressTracker
          customerCaseId={customerCaseId}
          showActions={false} // Read-only mode for final stage
        />
      </Box>
      
      <CustomerQuoteCompilationManager
        open={showManager}
        onClose={() => setShowManager(false)}
        customerCaseId={customerCaseId}
        onCompilationComplete={(data) => {
          onCompilationComplete(data);
          setShowManager(false); // Close after completion
        }}
        onWorkflowTransition={onWorkflowTransition}
        currentWorkflowState={currentWorkflowState}
      />
    </Box>
  );
};

export default CustomerPreprocessingManager;
